/**
 * Give a `$$` closing fence its own line when the source put text after it.
 *
 * `micromark-extension-math` accepts a closing fence only when the `$$` run is
 * followed by end of line (`math-flow.js` → `afterSequenceClose`). The agent
 * routinely appends a citation to the fence:
 *
 *     $$
 *     \begin{array}{l} … \end{array}
 *     $$ [ID:2_v0]
 *
 * so the block never closes, the rest of the document is swallowed as math, and
 * KaTeX — handed TeX that ends in `$$ [ID:2_v0]` — throws. `rehype-katex` then
 * prints the raw source and the formula shows up on screen as plain LaTeX.
 *
 * Splitting the line is enough: the math block closes, and the trailing text
 * becomes the paragraph that follows it, where the citation markup already
 * knows how to render `[ID:…]`.
 */
const FENCE = /^( {0,3})(\$\$+)(.*)$/;
const CODE_FENCE = /^ {0,3}(`{3,}|~{3,})/;

export const normalizeMathFences = (markdown: string): string => {
  if (!markdown.includes("$$")) return markdown;

  const lines = markdown.split("\n");
  const out: string[] = [];
  let openSize = 0; // 0 when not inside a display-math block
  let inCode = false;

  for (const line of lines) {
    if (CODE_FENCE.test(line)) {
      // A code fence inside display math is just math content; elsewhere it
      // opens or closes a code block, where `$$` is literal text.
      if (openSize === 0) inCode = !inCode;
      out.push(line);
      continue;
    }

    const match = inCode ? null : FENCE.exec(line);
    if (!match) {
      out.push(line);
      continue;
    }

    const [, indent, sequence, rest] = match;

    if (openSize === 0) {
      // An opening fence's meta may not contain `$`, so `$$x$$` on one line is
      // inline math, not a block — leave it for the text-math tokenizer.
      if (!rest.includes("$")) openSize = sequence.length;
      out.push(line);
      continue;
    }

    if (sequence.length < openSize) {
      // Too short to close this block: ordinary math content.
      out.push(line);
      continue;
    }

    openSize = 0;

    if (rest.trim() === "") {
      // Already a valid closing fence (trailing whitespace is allowed).
      out.push(line);
      continue;
    }

    out.push(indent + sequence);
    out.push(rest.trim());
  }

  return out.join("\n");
};
